import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import {
  CERTIFICATION_INDUSTRIES,
  CERTIFICATION_LEVELS,
  CERTIFICATION_PROGRAMS,
  AGENCY_ENTERPRISE_PLANS,
  DEMO_USER,
  levelColor,
  INDUSTRY_SLUGS,
  type CertificationLevel,
  type CertificationProgram,
  type PricingLicense,
} from "../lib/certifications";
import { fetchPrograms, fetchLicenses, enroll } from "../lib/sentinel-api";
import { validateMapping } from "../lib/sentinel-mapping";
import { fetchOrgInfo, fetchUserIndustryPrefs } from "../lib/portal-api";
import { INDUSTRIES } from "../lib/industries";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

export default function CertificationsCatalogPage() {
  const [industryFilter, setIndustryFilter] = useState("All");
  const [levelFilter, setLevelFilter] = useState("All");
  const [viewType, setViewType] = useState<"individual" | "agency">("individual");
  const [programs, setPrograms] = useState<CertificationProgram[]>(CERTIFICATION_PROGRAMS);
  const [licenses, setLicenses] = useState<PricingLicense[]>(AGENCY_ENTERPRISE_PLANS);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [enrollResult, setEnrollResult] = useState<{ programId: string; url?: string; enrolled: boolean; error?: string } | null>(null);
  const { identity } = useIdentity();

  const user = DEMO_USER;
  const isLoggedIn = user.id !== "";

  useEffect(() => {
    Promise.all([fetchPrograms(), fetchLicenses()]).then(([progs, lics]) => {
      if (progs.length) setPrograms(progs);
      if (lics.length) setLicenses(lics);
      setLoading(false);
    });
    // Load industry preferences
    if (identity.organizationId) {
      fetchOrgInfo(identity.organizationId).then((info) => {
        if (info?.industry_preferences && info.industry_preferences.length > 0) {
          // Map industry slugs to certification industry names
          const prefNames = info.industry_preferences
            .map((slug) => INDUSTRIES.find((i) => i.slug === slug)?.name)
            .filter(Boolean) as string[];
          if (prefNames.length > 0) {
            setIndustryFilter(prefNames[0]); // Default to first preference
          }
        }
      });
    } else if (identity.userId) {
      fetchUserIndustryPrefs(identity.userId).then((prefs) => {
        if (prefs && prefs.length > 0) {
          const prefNames = prefs
            .map((slug) => INDUSTRIES.find((i) => i.slug === slug)?.name)
            .filter(Boolean) as string[];
          if (prefNames.length > 0) {
            setIndustryFilter(prefNames[0]);
          }
        }
      });
    }
  }, [identity.organizationId, identity.userId]);

  const filtered = programs.filter((p) => {
    if (industryFilter !== "All" && p.industry !== industryFilter) return false;
    if (levelFilter !== "All" && p.level !== levelFilter) return false;
    return true;
  });

  async function handleEnroll(programId: string) {
    if (!isLoggedIn) return;
    const program = programs.find((p) => p.id === programId);
    if (program) {
      const mappingError = validateMapping(program.code);
      if (mappingError) {
        setEnrollResult({ programId, enrolled: false, error: mappingError });
        setEnrolling(null);
        return;
      }
    }
    setEnrolling(programId);
    const result = await enroll(user.id, programId);
    if (result?.checkoutNeeded && result.checkoutUrl) {
      setEnrollResult({ programId, url: result.checkoutUrl, enrolled: false });
    } else if (result?.enrolled) {
      setEnrollResult({ programId, enrolled: true });
    }
    setEnrolling(null);
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Certification Catalog</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Browse all Sentinel certification programs by industry and level. Enroll individually or purchase multi-seat plans for your organization.
        </p>
      </div>

      {/* Industry certification links */}
      <div style={{ marginBottom: "var(--space-4)", padding: "var(--space-3)", background: "var(--bg-subtle)", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}>
        <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: "0.75rem" }}>
          Browse certifications by industry:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
          {INDUSTRY_SLUGS.map((ind) => (
            <Link
              key={ind.slug}
              to={`/certifications/${ind.slug}`}
              style={{
                display: "inline-block",
                padding: "0.25rem 0.625rem",
                borderRadius: "var(--radius-full)",
                fontSize: "0.75rem",
                fontWeight: 500,
                background: "var(--surface)",
                border: "1px solid var(--border)",
                color: "var(--text-muted)",
                textDecoration: "none",
                transition: "all var(--transition-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--accent-400)";
                e.currentTarget.style.color = "var(--accent-700)";
                e.currentTarget.style.background = "var(--accent-50)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border)";
                e.currentTarget.style.color = "var(--text-muted)";
                e.currentTarget.style.background = "var(--surface)";
              }}
            >
              {ind.name} &rarr;
            </Link>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          marginBottom: "var(--space-4)",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <select
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          style={{
            padding: "0.5rem 0.75rem",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-strong)",
            background: "var(--surface)",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <option value="All">All Industries</option>
          {CERTIFICATION_INDUSTRIES.map((ind) => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </select>

        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          style={{
            padding: "0.5rem 0.75rem",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-strong)",
            background: "var(--surface)",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <option value="All">All Levels</option>
          {CERTIFICATION_LEVELS.map((lvl) => (
            <option key={lvl} value={lvl}>{lvl}</option>
          ))}
        </select>

        <div style={{ display: "flex", gap: "0.25rem", padding: "0.25rem", borderRadius: "var(--radius-md)", background: "var(--neutral-100)" }}>
          <button
            onClick={() => setViewType("individual")}
            style={{
              padding: "0.375rem 0.875rem",
              fontSize: "0.8125rem",
              fontWeight: 600,
              borderRadius: "var(--radius-sm)",
              background: viewType === "individual" ? "var(--surface)" : "transparent",
              border: "none",
              cursor: "pointer",
              color: viewType === "individual" ? "var(--text)" : "var(--text-muted)",
              boxShadow: viewType === "individual" ? "var(--shadow-sm)" : "none",
            }}
          >
            Individual
          </button>
          <button
            onClick={() => setViewType("agency")}
            style={{
              padding: "0.375rem 0.875rem",
              fontSize: "0.8125rem",
              fontWeight: 600,
              borderRadius: "var(--radius-sm)",
              background: viewType === "agency" ? "var(--surface)" : "transparent",
              border: "none",
              cursor: "pointer",
              color: viewType === "agency" ? "var(--text)" : "var(--text-muted)",
              boxShadow: viewType === "agency" ? "var(--shadow-sm)" : "none",
            }}
          >
            Agency / Enterprise
          </button>
        </div>
      </div>

      {/* Individual catalog grid */}
      {viewType === "individual" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "var(--space-3)",
          }}
        >
          {loading ? (
            <p className="text-muted" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "var(--space-5)" }}>Loading programs...</p>
          ) : (
            filtered.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                isLoggedIn={isLoggedIn}
                onEnroll={() => handleEnroll(program.id)}
                enrolling={enrolling === program.id}
                enrollResult={enrollResult?.programId === program.id ? enrollResult : null}
              />
            ))
          )}
        </div>
      )}

      {/* Agency / Enterprise plans */}
      {viewType === "agency" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: "var(--space-3)",
          }}
        >
          {licenses.filter((plan) => {
            if (industryFilter !== "All" && plan.industry !== industryFilter) return false;
            return true;
          }).map((plan) => (
            <div key={plan.id} className="card card-hover" style={{ padding: "var(--space-4)", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{plan.name}</h3>
                <span
                  style={{
                    padding: "0.125rem 0.5rem",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    background: plan.type === "enterprise" ? "var(--primary-100)" : "var(--accent-100)",
                    color: plan.type === "enterprise" ? "var(--primary-700)" : "var(--accent-700)",
                  }}
                >
                  {plan.type}
                </span>
              </div>
              <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}>{plan.industry}</p>
              <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                {plan.levels_covered.map((lvl) => (
                  <LevelPill key={lvl} level={lvl} />
                ))}
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>{plan.seats} seats</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 800, marginBottom: "0.75rem" }}>
                {plan.currency}{plan.price.toLocaleString()}
                <span style={{ fontSize: "0.875rem", fontWeight: 400, color: "var(--text-muted)" }}> / year</span>
              </div>
              <Link
                to={isLoggedIn ? "/licenses" : "/login"}
                className="btn btn-primary"
                style={{ marginTop: "auto", fontSize: "0.8125rem" }}
              >
                Purchase for Organization
              </Link>
            </div>
          ))}
        </div>
      )}
    </PlatformLayout>
  );
}

function ProgramCard({
  program,
  isLoggedIn,
  onEnroll,
  enrolling,
  enrollResult,
}: {
  program: CertificationProgram;
  isLoggedIn: boolean;
  onEnroll: () => void;
  enrolling: boolean;
  enrollResult: { url?: string; enrolled: boolean; error?: string } | null;
}) {
  return (
    <div className="card card-hover" style={{ overflow: "hidden", padding: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ position: "relative", height: "140px", overflow: "hidden" }}>
        <img src={program.image} alt={program.industry} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,22,40,0.1) 0%, rgba(10,22,40,0.7) 100%)" }} />
        <div style={{ position: "absolute", bottom: "0.625rem", left: "0.75rem", right: "0.75rem" }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 600, color: "rgba(255,255,255,0.85)", marginBottom: "0.125rem" }}>{program.industry}</div>
          <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>{program.title}</div>
        </div>
      </div>
      <div style={{ padding: "var(--space-3)", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", gap: "0.375rem", marginBottom: "0.5rem" }}>
          <LevelPill level={program.level} />
          <span style={{ padding: "0.125rem 0.5rem", borderRadius: "var(--radius-sm)", fontSize: "0.6875rem", fontWeight: 600, background: "var(--neutral-100)", color: "var(--text-muted)" }}>
            {program.term}
          </span>
        </div>
        <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem", flex: 1 }}>{program.description}</p>
        <div style={{ fontSize: "1.25rem", fontWeight: 800, marginBottom: "0.75rem" }}>
          {program.currency}{program.price}
        </div>
        {enrollResult?.error ? (
          <div style={{ padding: "0.5rem 0.75rem", borderRadius: "var(--radius-md)", background: "var(--error-50)", border: "1px solid var(--error-200)", fontSize: "0.8125rem", color: "var(--error-700)", textAlign: "center", marginBottom: "0.5rem" }}>
            {enrollResult.error}
          </div>
        ) : enrollResult?.enrolled ? (
          <div style={{ padding: "0.5rem 0.75rem", borderRadius: "var(--radius-md)", background: "var(--accent-50)", border: "1px solid var(--accent-200)", fontSize: "0.8125rem", color: "var(--accent-700)", textAlign: "center", marginBottom: "0.5rem" }}>
            &#10003; Enrolled! You now have access.
          </div>
        ) : enrollResult?.url ? (
          <div style={{ padding: "0.5rem 0.75rem", borderRadius: "var(--radius-md)", background: "var(--primary-50)", border: "1px solid var(--primary-200)", fontSize: "0.8125rem", marginBottom: "0.5rem" }}>
            <a href={enrollResult.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary-600)", fontWeight: 600 }}>
              Complete checkout &rarr;
            </a>
          </div>
        ) : null}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Link to={`/programs/${program.id}`} className="btn btn-secondary" style={{ flex: 1, fontSize: "0.8125rem", padding: "0.5rem 0.75rem" }}>
            View Details
          </Link>
          {isLoggedIn ? (
            <button
              onClick={onEnroll}
              disabled={enrolling}
              className="btn btn-primary"
              style={{ flex: 1, fontSize: "0.8125rem", padding: "0.5rem 0.75rem" }}
            >
              {enrolling ? "..." : "Enroll"}
            </button>
          ) : (
            <Link to="/login" className="btn btn-primary" style={{ flex: 1, fontSize: "0.8125rem", padding: "0.5rem 0.75rem" }}>
              Enroll
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
